import {
  type Arc,
  type ChargeState,
  type FighterState,
  type Limb,
  type MatchPhase,
  type MatchState,
  PART_COUNT,
  type ParryState,
  type QueuedStrike,
  type StrikePhase,
  type StrikeState,
  TWO_PI,
  type Vec3,
  wrapAngle,
  type Zone,
} from "@meleeroyale/sim";
import { Reader, Writer } from "./bytes.ts";
import { MessageType } from "./types.ts";

const FLAG_ALIVE = 1;
const FLAG_GUARD = 2;
const FLAG_EXHAUSTED = 4;
const FLAG_BOT = 8;
const FLAG_STEPPING = 16;
const FLAG_SOUTHPAW = 32;
const FLAG_CHECKING = 64;
/** Twist runs −2..2 blade units; one byte at 1/60 resolution. */
const TWIST_SCALE = 60;
const CAGE_WARNING = 1;
const NO_LIMB = 255;
const PHASES: MatchPhase[] = ["countdown", "fight", "ended"];
const STRIKE_PHASES: StrikePhase[] = ["windup", "throw", "recovery"];

export interface StrikeTelegraph {
  phase: StrikePhase;
  limb: Limb;
  arc: Arc;
}

/** Everything a client needs to render any fighter. */
export interface FighterSnapshot {
  id: number;
  alive: boolean;
  isBot: boolean;
  guard: boolean;
  exhausted: boolean;
  pos: Vec3;
  impulse: Vec3;
  facing: number;
  lookYaw: number;
  lookPitch: number;
  duck: number;
  /** Slip, -1 (fighter's left) .. 1 (right). */
  lean: number;
  /** Stance, +1 orthodox .. −1 southpaw (in between mid-switch). */
  stance: number;
  /** The stance being held or switched to. */
  southpaw: boolean;
  /** Torso turn from strikes in flight, blade units. */
  twist: number;
  /** Lead knee up, checking. */
  checking: boolean;
  hp: number[];
  stamina: number;
  step: { ticksLeft: number; dir: Vec3 } | null;
  crushTicks: number;
  prevButtons: number;
  damageDealt: number;
  lastHitBy: number;
  handL: Vec3;
  handR: Vec3;
  footL: Vec3;
  footR: Vec3;
  chargeLimb: Limb | null;
  /** 0..1 of the full charge. */
  chargeLevel: number;
  strikeArm: StrikeTelegraph | null;
  strikeLeg: StrikeTelegraph | null;
  parryArm: 0 | 1 | null;
  stagger: number;
  /** Full combat state, present only for the receiving player's own fighter. */
  combat?: OwnerCombat;
}

export interface OwnerCombat {
  chargeArm: ChargeState | null;
  chargeLeg: ChargeState | null;
  strikeArm: StrikeState | null;
  strikeLeg: StrikeState | null;
  /** Combo presses waiting their turn, and strikes started in this combo. */
  queue: QueuedStrike[];
  chain: number;
  parry: ParryState | null;
  stagger: number;
  initiativeLock: number;
}

export interface Snapshot {
  tick: number;
  /** Last input tick the server applied for the receiving client. */
  ackTick: number;
  phase: MatchPhase;
  clockTicks: number;
  cage: { r: number; targetR: number; phase: number; warning: boolean };
  winner: number;
  ownerId: number;
  fighters: FighterSnapshot[];
}

/** Encodes the whole match for one receiver; `ownerId` gets its full combat state appended. */
export function encodeSnapshot(
  state: MatchState,
  ackTick: number,
  ownerId = -1,
): Uint8Array<ArrayBuffer> {
  const w = new Writer(64 + 80 * state.fighters.length);
  w.u8(MessageType.Snapshot);
  w.u32(state.tick);
  w.u32(ackTick);
  w.u8(PHASES.indexOf(state.phase));
  w.u16(Math.max(0, state.clockTicks));
  w.u16(state.cage.r * 100);
  w.u16(state.cage.targetR * 100);
  w.u8(state.cage.phase);
  w.u8(state.cage.warning ? CAGE_WARNING : 0);
  w.i8(state.winner);
  w.i8(ownerId);
  w.u8(state.fighters.length);
  for (const f of state.fighters) encodeFighter(w, f);
  const owner = ownerId >= 0 ? state.fighters[ownerId] : undefined;
  if (owner) encodeOwnerCombat(w, owner);
  return w.bytes();
}

function encodeFighter(w: Writer, f: FighterState): void {
  w.u8(f.id);
  w.u8(
    (f.alive ? FLAG_ALIVE : 0) |
      (f.guard ? FLAG_GUARD : 0) |
      (f.exhausted ? FLAG_EXHAUSTED : 0) |
      (f.isBot ? FLAG_BOT : 0) |
      (f.step ? FLAG_STEPPING : 0) |
      (f.southpaw ? FLAG_SOUTHPAW : 0) |
      (f.checking ? FLAG_CHECKING : 0),
  );
  w.i16(f.pos.x * 100);
  w.i16(f.pos.z * 100);
  w.u16(angleToU16(f.facing));
  w.u16(f.lookYaw);
  w.i8(f.lookPitch);
  w.u8(f.duck * 255);
  w.i8(f.lean * 127);
  w.i8(f.stance * 127);
  w.i8(f.twist * TWIST_SCALE);
  for (let i = 0; i < PART_COUNT; i += 1) w.u16((f.hp[i] ?? 0) * 10);
  w.u8(f.stamina);
  w.i16(f.impulse.x * 1000);
  w.i16(f.impulse.z * 1000);
  if (f.step) {
    w.u8(f.step.ticksLeft);
    w.u16(angleToU16(Math.atan2(f.step.dir.x, f.step.dir.z)));
  } else {
    w.u8(0);
    w.u16(0);
  }
  w.u8(f.crushTicks);
  w.u16(f.prevButtons);
  w.u16(f.damageDealt);
  w.i8(f.lastHitBy);
  vec(w, f.handL);
  vec(w, f.handR);
  vec(w, f.footL);
  vec(w, f.footR);
  const charge = f.chargeArm ?? f.chargeLeg;
  w.u8(charge ? charge.limb : NO_LIMB);
  w.u8(charge ? Math.min(255, (charge.ticks / 18) * 255) : 0);
  w.u8(telegraphByte(f.strikeArm));
  w.u8(telegraphByte(f.strikeLeg));
  w.u8(f.parry ? 1 + f.parry.arm : 0);
  w.u8(f.stagger);
}

function telegraphByte(s: StrikeState | null): number {
  if (!s) return 0;
  return 1 | (STRIKE_PHASES.indexOf(s.phase) << 1) | ((s.limb & 1) << 3) | (s.arc << 4);
}

function encodeOwnerCombat(w: Writer, f: FighterState): void {
  charge(w, f.chargeArm);
  charge(w, f.chargeLeg);
  strike(w, f.strikeArm);
  strike(w, f.strikeLeg);
  w.u8(f.chain);
  w.u8(f.queue.length);
  for (const q of f.queue) w.u8(linkByte(q));
  if (f.parry) {
    w.u8(1);
    w.u8(f.parry.arm);
    w.u8(f.parry.ticksLeft);
    w.u8(f.parry.total);
  } else w.u8(0);
  w.u8(f.stagger);
  w.u8(f.initiativeLock);
}

function charge(w: Writer, c: ChargeState | null): void {
  if (!c) {
    w.u8(0);
    return;
  }
  w.u8(1);
  w.u8(linkByte(c));
  w.u8(c.ticks);
}

/** Limb, arc and zone in one byte: 2 bits each. */
function linkByte(l: { limb: Limb; arc: Arc; zone: Zone }): number {
  return l.limb | (l.arc << 2) | (l.zone << 4);
}

function linkOf(b: number): QueuedStrike {
  return { limb: asLimb(b & 3), arc: asArc((b >> 2) & 3), zone: asZone((b >> 4) & 3) };
}

function strike(w: Writer, s: StrikeState | null): void {
  if (!s) {
    w.u8(0);
    return;
  }
  w.u8(1);
  w.u8(s.limb);
  w.u8(s.arc);
  w.u8(STRIKE_PHASES.indexOf(s.phase));
  w.u8(s.ticks);
  w.u8(s.charge * 255);
  w.u8(s.windupTicks);
  w.u8(s.throwTicks);
  w.u8(s.recoveryTicks);
  vec(w, s.origin);
  vec(w, s.control);
  vec(w, s.aim);
  vecCm(w, s.aimWorld);
  vec(w, s.rest);
  vec(w, s.end);
  vecCm(w, s.tipPrev);
  w.u16(s.progress * 10000);
  w.u16(s.pathLength * 1000);
  w.i8(s.lead * 127);
  w.u8((s.hit ? 1 : 0) | (s.whiffed ? 2 : 0) | (s.blocked ? 4 : 0));
}

export function decodeSnapshot(bytes: Uint8Array): Snapshot | null {
  try {
    const r = new Reader(bytes);
    if (r.u8() !== MessageType.Snapshot) return null;
    const tick = r.u32();
    const ackTick = r.u32();
    const phase = PHASES[r.u8()];
    if (!phase) return null;
    const clockTicks = r.u16();
    const cageR = r.u16() / 100;
    const targetR = r.u16() / 100;
    const cagePhase = r.u8();
    const warning = (r.u8() & CAGE_WARNING) !== 0;
    const winner = r.i8();
    const ownerId = r.i8();
    const n = r.u8();
    const fighters: FighterSnapshot[] = [];
    for (let i = 0; i < n; i += 1) fighters.push(decodeFighter(r));
    const owner = ownerId >= 0 ? fighters.find((f) => f.id === ownerId) : undefined;
    if (owner) owner.combat = decodeOwnerCombat(r);
    if (r.remaining !== 0) return null;
    return {
      tick,
      ackTick,
      phase,
      clockTicks,
      cage: { r: cageR, targetR, phase: cagePhase, warning },
      winner,
      ownerId,
      fighters,
    };
  } catch {
    return null;
  }
}

function decodeFighter(r: Reader): FighterSnapshot {
  const id = r.u8();
  const flags = r.u8();
  const pos = { x: r.i16() / 100, y: 0, z: r.i16() / 100 };
  const facing = u16ToAngle(r.u16());
  const lookYaw = r.u16();
  const lookPitch = r.i8();
  const duck = r.u8() / 255;
  const lean = r.i8() / 127;
  const stance = r.i8() / 127;
  const twist = r.i8() / TWIST_SCALE;
  const hp: number[] = [];
  for (let i = 0; i < PART_COUNT; i += 1) hp.push(r.u16() / 10);
  const stamina = r.u8();
  const impulse = { x: r.i16() / 1000, y: 0, z: r.i16() / 1000 };
  const stepTicks = r.u8();
  const stepAngle = u16ToAngle(r.u16());
  const crushTicks = r.u8();
  const prevButtons = r.u16();
  const damageDealt = r.u16();
  const lastHitBy = r.i8();
  const handL = vecOf(r);
  const handR = vecOf(r);
  const footL = vecOf(r);
  const footR = vecOf(r);
  const chargeLimbRaw = r.u8();
  const chargeLevel = r.u8() / 255;
  const strikeArm = telegraphOf(r.u8());
  const strikeLeg = telegraphOf(r.u8());
  const parryRaw = r.u8();
  const stagger = r.u8();
  const stepping = (flags & FLAG_STEPPING) !== 0;
  return {
    id,
    alive: (flags & FLAG_ALIVE) !== 0,
    isBot: (flags & FLAG_BOT) !== 0,
    guard: (flags & FLAG_GUARD) !== 0,
    exhausted: (flags & FLAG_EXHAUSTED) !== 0,
    pos,
    impulse,
    facing,
    lookYaw,
    lookPitch,
    duck,
    lean,
    stance,
    southpaw: (flags & FLAG_SOUTHPAW) !== 0,
    twist,
    checking: (flags & FLAG_CHECKING) !== 0,
    hp,
    stamina,
    step: stepping
      ? { ticksLeft: stepTicks, dir: { x: Math.sin(stepAngle), y: 0, z: Math.cos(stepAngle) } }
      : null,
    crushTicks,
    prevButtons,
    damageDealt,
    lastHitBy,
    handL,
    handR,
    footL,
    footR,
    chargeLimb: chargeLimbRaw === NO_LIMB ? null : asLimb(chargeLimbRaw),
    chargeLevel,
    strikeArm,
    strikeLeg,
    parryArm: parryRaw === 0 ? null : parryRaw === 1 ? 0 : 1,
    stagger,
  };
}

function telegraphOf(b: number): StrikeTelegraph | null {
  if ((b & 1) === 0) return null;
  const phase = STRIKE_PHASES[(b >> 1) & 3] ?? "throw";
  return { phase, limb: asLimb((b >> 3) & 1), arc: asArc((b >> 4) & 3) };
}

function decodeOwnerCombat(r: Reader): OwnerCombat {
  const chargeArm = chargeOf(r);
  const chargeLeg = chargeOf(r);
  const strikeArm = strikeOf(r);
  const strikeLeg = strikeOf(r);
  const chain = r.u8();
  const queue: QueuedStrike[] = [];
  for (let n = r.u8(), i = 0; i < n; i += 1) queue.push(linkOf(r.u8()));
  const hasParry = r.u8();
  const parry: ParryState | null = hasParry
    ? { arm: r.u8() === 0 ? 0 : 1, ticksLeft: r.u8(), total: r.u8() }
    : null;
  const stagger = r.u8();
  const initiativeLock = r.u8();
  return {
    chargeArm,
    chargeLeg,
    strikeArm,
    strikeLeg,
    queue,
    chain,
    parry,
    stagger,
    initiativeLock,
  };
}

function chargeOf(r: Reader): ChargeState | null {
  if (r.u8() === 0) return null;
  const link = linkOf(r.u8());
  return { ...link, ticks: r.u8() };
}

function strikeOf(r: Reader): StrikeState | null {
  if (r.u8() === 0) return null;
  const limb = asLimb(r.u8());
  const arc = asArc(r.u8());
  const phase = STRIKE_PHASES[r.u8()] ?? "throw";
  const ticks = r.u8();
  const charge = r.u8() / 255;
  const windupTicks = r.u8();
  const throwTicks = r.u8();
  const recoveryTicks = r.u8();
  const origin = vecOf(r);
  const control = vecOf(r);
  const aim = vecOf(r);
  const aimWorld = vecCmOf(r);
  const rest = vecOf(r);
  const end = vecOf(r);
  const tipPrev = vecCmOf(r);
  const progress = r.u16() / 10000;
  const pathLength = r.u16() / 1000;
  const lead = r.i8() / 127;
  const flags = r.u8();
  return {
    limb,
    arc,
    phase,
    ticks,
    charge,
    windupTicks,
    throwTicks,
    recoveryTicks,
    origin,
    control,
    aim,
    aimWorld,
    rest,
    end,
    tipPrev,
    progress,
    pathLength,
    lead,
    hit: (flags & 1) !== 0,
    blocked: (flags & 4) !== 0,
    whiffed: (flags & 2) !== 0,
  };
}

/** Copies a fighter snapshot into a live FighterState (client prediction, bots). */
export function applyFighterSnapshot(f: FighterState, s: FighterSnapshot): void {
  f.alive = s.alive;
  f.isBot = s.isBot;
  f.guard = s.guard;
  f.exhausted = s.exhausted;
  f.pos = { ...s.pos };
  f.impulse = { ...s.impulse };
  f.facing = s.facing;
  f.lookYaw = s.lookYaw;
  f.lookPitch = s.lookPitch;
  f.duck = s.duck;
  f.lean = s.lean;
  f.stance = s.stance;
  f.southpaw = s.southpaw;
  f.twist = s.twist;
  f.checking = s.checking;
  for (let i = 0; i < PART_COUNT; i += 1) f.hp[i] = s.hp[i] ?? 0;
  f.stamina = s.stamina;
  f.step = s.step ? { ticksLeft: s.step.ticksLeft, dir: { ...s.step.dir } } : null;
  f.crushTicks = s.crushTicks;
  f.prevButtons = s.prevButtons;
  f.damageDealt = s.damageDealt;
  f.lastHitBy = s.lastHitBy;
  f.handL = { ...s.handL };
  f.handR = { ...s.handR };
  f.footL = { ...s.footL };
  f.footR = { ...s.footR };
  f.stagger = s.stagger;
  if (s.combat) {
    f.chargeArm = s.combat.chargeArm ? { ...s.combat.chargeArm } : null;
    f.chargeLeg = s.combat.chargeLeg ? { ...s.combat.chargeLeg } : null;
    f.strikeArm = s.combat.strikeArm ? cloneStrike(s.combat.strikeArm) : null;
    f.strikeLeg = s.combat.strikeLeg ? cloneStrike(s.combat.strikeLeg) : null;
    f.queue = s.combat.queue.map((q) => ({ ...q }));
    f.chain = s.combat.chain;
    f.parry = s.combat.parry ? { ...s.combat.parry } : null;
    f.initiativeLock = s.combat.initiativeLock;
  }
}

function cloneStrike(s: StrikeState): StrikeState {
  return {
    ...s,
    origin: { ...s.origin },
    control: { ...s.control },
    aim: { ...s.aim },
    aimWorld: { ...s.aimWorld },
    rest: { ...s.rest },
    end: { ...s.end },
    tipPrev: { ...s.tipPrev },
  };
}

function asLimb(n: number): Limb {
  return n === 0 ? 0 : n === 1 ? 1 : n === 2 ? 2 : 3;
}
function asArc(n: number): Arc {
  return n === 0 ? 0 : n === 1 ? 1 : 2;
}
function asZone(n: number): Zone {
  return n === 0 ? 0 : n === 1 ? 1 : 2;
}
function vec(w: Writer, v: Vec3): void {
  w.i16(v.x * 1000);
  w.i16(v.y * 1000);
  w.i16(v.z * 1000);
}
function vecCm(w: Writer, v: Vec3): void {
  w.i16(v.x * 100);
  w.i16(v.y * 100);
  w.i16(v.z * 100);
}
function vecOf(r: Reader): Vec3 {
  return { x: r.i16() / 1000, y: r.i16() / 1000, z: r.i16() / 1000 };
}
function vecCmOf(r: Reader): Vec3 {
  return { x: r.i16() / 100, y: r.i16() / 100, z: r.i16() / 100 };
}
function angleToU16(a: number): number {
  const t = wrapAngle(a) / TWO_PI + 0.5;
  return Math.round(t * 65535);
}
function u16ToAngle(u: number): number {
  return (u / 65535 - 0.5) * TWO_PI;
}
